import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/store';
import type { BattleReplayResponse } from '../../lib/types';
import { Bar, Button, Modal } from '../ui';

/**
 * The battle replay — design PART 14 and 43.
 *
 * The server sends *events*, not video and not a result to trust: the battle
 * was already resolved server-side and regenerated from its seed, so this
 * component only animates a transcript. Nothing here can change an outcome,
 * which is why the replay endpoint is safe to make public.
 *
 * Playback is a simple interval over the event list rather than a timeline
 * engine. Target is 30–90 seconds for a whole battle, and the tick is fast
 * enough that a 30-turn battle lands in that window while still letting each
 * hit register.
 */

const TICK_MS = 420;

interface Side {
  name: string;
  speciesId: number;
  isShiny: boolean;
  level: number;
  hp: number;
  maxHp: number;
  status: string | null;
}

export function BattleReplay({ battleId, onClose }: { battleId: string; onClose: () => void }) {
  const state = useAsync<BattleReplayResponse>(() => api.get(`/wars/battle/${battleId}`), [battleId]);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);

  const events = state.data?.events ?? [];

  useEffect(() => {
    if (!playing || events.length === 0) return;
    if (cursor >= events.length) return;
    const timer = window.setTimeout(() => setCursor((c) => c + 1), TICK_MS);
    return () => window.clearTimeout(timer);
  }, [cursor, playing, events.length]);

  // Keep the newest line in view without yanking the whole modal about.
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
  }, [cursor]);

  /**
   * Replays the transcript up to the cursor to derive the current board.
   *
   * Recomputed from scratch each tick rather than mutated incrementally, so
   * scrubbing backwards works for free and the displayed state can never drift
   * from the events that produced it.
   */
  const board = useMemo(() => {
    let a: Side | null = null;
    let b: Side | null = null;
    const lines: string[] = [];

    for (const event of events.slice(0, cursor)) {
      const setSide = (side: 'a' | 'b', next: Side) => (side === 'a' ? (a = next) : (b = next));
      const get = (side: 'a' | 'b') => (side === 'a' ? a : b);

      switch (event.t) {
        case 'send':
          setSide(event.side, {
            name: event.name,
            speciesId: event.speciesId,
            isShiny: event.isShiny,
            level: event.level,
            hp: event.hp,
            maxHp: event.maxHp,
            status: null,
          });
          lines.push(`${event.side === 'a' ? '▲' : '▼'} ${event.name} (Lv ${event.level}) is sent out.`);
          break;
        case 'move':
          lines.push(`${get(event.side)?.name ?? '?'} used ${event.name}.`);
          break;
        case 'miss':
          lines.push('It missed.');
          break;
        case 'damage': {
          const target = event.side === 'a' ? 'b' : 'a';
          const current = get(target);
          if (current) setSide(target, { ...current, hp: event.hp });
          lines.push(
            `${event.crit ? 'A critical hit! ' : ''}${current?.name ?? '?'} took ${event.amount}.` +
              (event.note ? ` ${event.note}` : ''),
          );
          break;
        }
        case 'heal': {
          const current = get(event.side);
          if (current) setSide(event.side, { ...current, hp: event.hp });
          lines.push(`${current?.name ?? '?'} recovered ${event.amount}.`);
          break;
        }
        case 'stat':
          lines.push(
            `${get(event.side)?.name ?? '?'}'s ${event.stat} ${event.delta > 0 ? 'rose' : 'fell'}.`,
          );
          break;
        case 'status': {
          const current = get(event.side);
          if (current) setSide(event.side, { ...current, status: event.status });
          lines.push(`${current?.name ?? '?'} was ${event.status}ed.`);
          break;
        }
        case 'statusTick': {
          const current = get(event.side);
          if (current) setSide(event.side, { ...current, hp: event.hp });
          lines.push(`${current?.name ?? '?'} was hurt by its ${event.status}.`);
          break;
        }
        case 'faint':
          lines.push(`${get(event.side)?.name ?? '?'} fainted!`);
          break;
        case 'end':
          lines.push(
            event.winner === 'draw'
              ? 'The battle ended in a draw.'
              : `${event.winner === 'a' ? 'The attacker' : 'The defender'} won after ${event.turns} turns.`,
          );
          break;
        default:
          break;
      }
    }

    return { a, b, lines };
  }, [events, cursor]);

  const done = cursor >= events.length && events.length > 0;

  return (
    <Modal onClose={onClose} wide labelledBy="replay-title">
      <div className="stack">
        <h2 id="replay-title" className="page-head__title">
          {state.data ? `${state.data.attacker} vs ${state.data.defender}` : 'Battle'}
        </h2>

        {!state.data ? (
          <p className="tiny muted">Loading the replay…</p>
        ) : (
          <>
            <div className="row row--between" style={{ gap: 'var(--gap)' }}>
              <Fighter side={board.a} label={state.data.attacker} />
              <Fighter side={board.b} label={state.data.defender} mirrored />
            </div>

            <div ref={logRef} className="pxwell battle-log">
              {board.lines.map((line, index) => (
                <div key={index} className="tiny">
                  {line}
                </div>
              ))}
            </div>

            <div className="row row--between">
              <div className="row" style={{ gap: 6 }}>
                <Button size="sm" onClick={() => setPlaying((p) => !p)} disabled={done}>
                  {playing ? 'Pause' : 'Play'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setCursor(events.length);
                    setPlaying(false);
                  }}
                >
                  Skip to end
                </Button>
              </div>
              {done && (
                <span className="chip">
                  {state.data.winner === 'a' ? 'Victory' : state.data.winner === 'b' ? 'Defeat' : 'Draw'}
                  {state.data.pointsAwarded > 0 && ` · +${state.data.pointsAwarded} pts`}
                  {state.data.wasUpset && ' · upset!'}
                </span>
              )}
            </div>

            {done && state.data.diminishedTo < 1000 && (
              <p className="tiny muted">
                Points were reduced to {state.data.diminishedTo / 10}% because this defender has
                already been attacked. Spread attacks around for full value.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function Fighter({ side, label, mirrored }: { side: Side | null; label: string; mirrored?: boolean }) {
  return (
    <div className="stack-sm" style={{ flex: 1, textAlign: mirrored ? 'right' : 'left' }}>
      <div className="tiny muted">{label}</div>
      {side ? (
        <>
          <div className="row" style={{ gap: 6, justifyContent: mirrored ? 'flex-end' : 'flex-start' }}>
            <img
              className="pixel"
              src={`/sprites/pokemon/${side.isShiny ? 'shiny/' : ''}${side.speciesId}.png`}
              alt=""
              width={56}
              height={56}
            />
            <div>
              <div className="tiny">
                {side.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
                {side.name}
              </div>
              <div className="tiny muted">Lv {side.level}</div>
            </div>
          </div>
          <Bar ratio={side.maxHp > 0 ? side.hp / side.maxHp : 0} variant="egg" />
          <div className="tiny muted">
            {side.hp} / {side.maxHp}
            {side.status && ` · ${side.status}`}
          </div>
        </>
      ) : (
        <div className="tiny muted">Waiting…</div>
      )}
    </div>
  );
}
